import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SecurityIcon from '@mui/icons-material/Security';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GoogleIcon from '@mui/icons-material/Google';
import { QRCodeSVG } from 'qrcode.react';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  EmailAuthProvider,
  multiFactor,
  reauthenticateWithCredential,
  sendEmailVerification,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import { linkGoogleCalendarScope, extractGoogleOAuthTokens } from '../lib/googleAuth';
import { saveCalendarRefreshTokenToServer } from '../utils/calendarSync';
import { canUseRichText } from '../lib/userRoleHierarchy';
import { resolveRichTextEnabled } from '../lib/richText';

import LoadingLockScreen from './LoadingLockScreen';

function optionalString(v) {
  const t = (v || '').trim();
  return t.length ? t : null;
}

const MFA_DISPLAY_NAME = 'Authenticator app';
const ISSUER_NAME = 'Shifty PM';

function getTotpEnrollment(user) {
  if (!user) return null;
  return multiFactor(user).enrolledFactors.find((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) || null;
}

const UserProfile = ({ onSaved }) => {
  const uid = auth.currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [role, setRole] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');

  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [totpEnrolled, setTotpEnrolled] = useState(false);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaStep, setMfaStep] = useState('password');
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaQrUri, setMfaQrUri] = useState('');
  const [mfaSecretKey, setMfaSecretKey] = useState('');
  const totpSecretRef = useRef(null);

  const [unenrollOpen, setUnenrollOpen] = useState(false);
  const [unenrollPassword, setUnenrollPassword] = useState('');
  const [verifySending, setVerifySending] = useState(false);

  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [enableRichTextEditor, setEnableRichTextEditor] = useState(true);

  const refreshMfaState = useCallback(() => {
    const u = auth.currentUser;
    setTotpEnrolled(Boolean(u && getTotpEnrollment(u)));
    setEmailVerified(Boolean(u?.emailVerified));
    setAuthEmail(u?.email || '');
  }, []);

  const loadProfile = useCallback(
    async ({ silent } = {}) => {
      if (!uid) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const u = auth.currentUser;
        setAuthEmail(u?.email || '');
        setEmailVerified(Boolean(u?.emailVerified));
        setTotpEnrolled(Boolean(u && getTotpEnrollment(u)));

        if (snap.exists()) {
          const d = snap.data();
          const roleVal = d.role || '';
          setRole(roleVal);
          setAvatarUrl(d.avatarUrl || d.photoURL || '');
          setFullName(d.fullName || d.displayName || '');
          setFirstName(d.firstName || '');
          setLastName(d.lastName || '');
          setPhone(d.phone || '');
          setContactEmail(d.contactEmail || '');
          setEnableRichTextEditor(
            canUseRichText(roleVal)
              ? resolveRichTextEnabled({ ...d, role: roleVal })
              : false,
          );
          setCalendarConnected(Boolean(d.integrations?.googleCalendar?.connected));
        } else {
          setRole('');
          setAvatarUrl('');
          setFullName('');
          setFirstName('');
          setLastName('');
          setPhone('');
          setContactEmail('');
          setEnableRichTextEditor(false);
          setCalendarConnected(false);
        }
      } catch (e) {
        console.error('UserProfile load error:', e);
        setError(e.message || 'Could not load profile.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [uid],
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const displayNameForDoc = () => {
    const fn = optionalString(fullName);
    if (fn) return fn;
    const parts = [optionalString(firstName), optionalString(lastName)].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!uid) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const dn = displayNameForDoc();
      const payload = {
        fullName: optionalString(fullName) ? fullName.trim() : deleteField(),
        firstName: optionalString(firstName) ? firstName.trim() : deleteField(),
        lastName: optionalString(lastName) ? lastName.trim() : deleteField(),
        phone: optionalString(phone) ? phone.trim() : deleteField(),
        contactEmail: optionalString(contactEmail) ? contactEmail.trim() : deleteField(),
        enableRichTextEditor: canUseRichText(role)
          ? enableRichTextEditor
          : deleteField(),
        richTextEnabled: canUseRichText(role)
          ? enableRichTextEditor
          : deleteField(),
      };
      if (dn) {
        payload.displayName = dn;
      } else {
        payload.displayName = deleteField();
      }

      await updateDoc(doc(db, 'users', uid), payload);
      setMessage('Profile saved.');
      await loadProfile({ silent: true });
      onSaved?.();
    } catch (e) {
      console.error('UserProfile save error:', e);
      setError(e.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uid) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setUploadingPhoto(true);
    setError(null);
    setMessage(null);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const safeExt = /^[a-z0-9]+$/i.test(ext) ? ext.toLowerCase() : 'jpg';
      const storageRef = ref(storage, `avatars/${uid}/avatar.${safeExt}`);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', uid), { avatarUrl: url });
      setAvatarUrl(url);
      setMessage('Photo updated.');
      onSaved?.();
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError(
        err.code === 'storage/unauthorized'
          ? 'Upload blocked by storage rules. Deploy rules that allow avatars/{userId}/...'
          : err.message || 'Could not upload photo.',
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openMfaEnroll = () => {
    setError(null);
    setMfaPassword('');
    setMfaCode('');
    setMfaQrUri('');
    setMfaSecretKey('');
    totpSecretRef.current = null;
    setMfaStep('password');
    setMfaDialogOpen(true);
  };

  const closeMfaDialog = () => {
    setMfaDialogOpen(false);
    setMfaPassword('');
    setMfaCode('');
    setMfaQrUri('');
    setMfaSecretKey('');
    totpSecretRef.current = null;
    setMfaStep('password');
  };

  const handleMfaPasswordContinue = async () => {
    const u = auth.currentUser;
    if (!u?.email || !mfaPassword) {
      setError('Enter your current password.');
      return;
    }
    setMfaBusy(true);
    setError(null);
    try {
      const cred = EmailAuthProvider.credential(u.email, mfaPassword);
      await reauthenticateWithCredential(u, cred);
      const session = await multiFactor(u).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      totpSecretRef.current = secret;
      const uri = secret.generateQrCodeUrl(u.email || 'user', ISSUER_NAME);
      setMfaQrUri(uri);
      setMfaSecretKey(secret.secretKey || '');
      setMfaStep('scan');
      setMfaPassword('');
    } catch (e) {
      console.error('MFA enroll setup:', e);
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('Incorrect password. Try again.');
      } else if (e.code === 'auth/too-many-requests') {
        setError('Too many attempts. Wait a moment and try again.');
      } else {
        setError(e.message || 'Could not start 2FA setup.');
      }
    } finally {
      setMfaBusy(false);
    }
  };

  const handleMfaConfirmEnroll = async () => {
    const u = auth.currentUser;
    const secret = totpSecretRef.current;
    const code = mfaCode.replace(/\s/g, '');
    if (!u || !secret) {
      setError('Session expired. Close this dialog and try again.');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setMfaBusy(true);
    setError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(u).enroll(assertion, MFA_DISPLAY_NAME);
      setMessage('Two-factor authentication is now enabled.');
      refreshMfaState();
      closeMfaDialog();
      onSaved?.();
    } catch (e) {
      console.error('MFA enroll:', e);
      if (e.code === 'auth/invalid-verification-code') {
        setError('Invalid code. Check the time on your device and try a new code.');
      } else {
        setError(e.message || 'Enrollment failed.');
      }
    } finally {
      setMfaBusy(false);
    }
  };

  const handleUnenrollTotp = async () => {
    const u = auth.currentUser;
    const factor = u && getTotpEnrollment(u);
    if (!u?.email || !factor || !unenrollPassword) {
      setError('Enter your password to disable 2FA.');
      return;
    }
    setMfaBusy(true);
    setError(null);
    try {
      const cred = EmailAuthProvider.credential(u.email, unenrollPassword);
      await reauthenticateWithCredential(u, cred);
      await multiFactor(u).unenroll(factor);
      setMessage('Two-factor authentication has been turned off.');
      setUnenrollOpen(false);
      setUnenrollPassword('');
      refreshMfaState();
      onSaved?.();
    } catch (e) {
      console.error('MFA unenroll:', e);
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('Incorrect password.');
      } else {
        setError(e.message || 'Could not disable 2FA.');
      }
    } finally {
      setMfaBusy(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setCalendarConnecting(true);
    setCalendarError(null);
    setMessage(null);
    try {
      const result = await linkGoogleCalendarScope(user);
      const { accessToken, refreshToken } = extractGoogleOAuthTokens(result);

      if (!refreshToken) {
        setCalendarError(
          'Google did not return a refresh token. Try again and approve all permissions (consent screen).',
        );
        return;
      }

      await saveCalendarRefreshTokenToServer({ refreshToken, accessToken });
      setCalendarConnected(true);
      setMessage('Google Calendar connected. Task sync can run in the background.');
      await loadProfile({ silent: true });
      onSaved?.();
    } catch (e) {
      console.error('Connect Google Calendar:', e);
      if (e.code === 'auth/popup-closed-by-user') {
        setCalendarError('Connection cancelled.');
      } else if (e.code === 'auth/multi-factor-auth-required') {
        setCalendarError('Complete MFA sign-in, then try connecting again.');
      } else {
        setCalendarError(e.message || 'Could not connect Google Calendar.');
      }
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleSendVerification = async () => {
    const u = auth.currentUser;
    if (!u) return;
    setVerifySending(true);
    setError(null);
    try {
      await sendEmailVerification(u);
      setMessage('Verification email sent. Check your inbox.');
    } catch (e) {
      setError(e.message || 'Could not send verification email.');
    } finally {
      setVerifySending(false);
    }
  };

  const avatarLabel =
    displayNameForDoc() ||
    optionalString(fullName) ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    authEmail ||
    '?';

  if (!uid) {
    return <Alert severity="warning">Not signed in.</Alert>;
  }

  if (loading) {
    return <LoadingLockScreen open />;
  }

  return (
    <Box component="form" onSubmit={handleSave}>
      <LoadingLockScreen open={uploadingPhoto || saving || mfaBusy || calendarConnecting} />
      <Stack spacing={3}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
          <Box sx={{ position: 'relative', alignSelf: 'center' }}>
            <Avatar src={avatarUrl || undefined} sx={{ width: 96, height: 96, fontSize: 40 }}>
              {avatarLabel.charAt(0).toUpperCase()}
            </Avatar>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              disabled={uploadingPhoto}
              component="label"
              aria-label="Upload profile photo"
            >
              {uploadingPhoto ? <CircularProgress size={20} /> : <PhotoCameraIcon fontSize="small" />}
              <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" gutterBottom>
              Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              All fields are optional. Use a full name <em>or</em> first and last — whatever fits your team.
            </Typography>
            {role && (
              <Chip
                size="small"
                label={String(role).replace(/_/g, ' ')}
                sx={{ textTransform: 'capitalize', mb: 1 }}
              />
            )}
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Sign-in email (read-only)
            </Typography>
            <Typography variant="body2">{authEmail || '—'}</Typography>
          </Box>
        </Stack>

        <TextField
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
          placeholder="e.g. Jordan Lee"
          helperText="Optional if you use first and last below instead."
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth />
          <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth />
        </Stack>

        <TextField
          label="Contact email"
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          fullWidth
          placeholder="Optional — shown on directory cards, etc."
          helperText="Does not change your login email."
        />

        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          placeholder="Optional"
        />

        {canUseRichText(role) && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={enableRichTextEditor}
                  onChange={(e) => setEnableRichTextEditor(e.target.checked)}
                  color="primary"
                />
              )}
              label="Enable Rich Text Editor"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 0.5 }}>
              When enabled, ticket descriptions and comments use formatting tools and file
              attachments. On by default for Super Admins, Company Admins, and Developers — turn
              off here to use plain text instead.
            </Typography>
          </Paper>
        )}

        <Divider />

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SecurityIcon color="primary" fontSize="small" />
            <Typography variant="h6">Security</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Add a second step at sign-in with an authenticator app (Google Authenticator, Authy, etc.). Requires Firebase
            Authentication with Identity Platform and TOTP enabled for your project.
          </Typography>

          {!emailVerified && (
            <Alert severity="warning">
              Verify your email before you can enroll in two-factor authentication.{' '}
              <Button size="small" onClick={handleSendVerification} disabled={verifySending} sx={{ ml: 1 }}>
                {verifySending ? 'Sending…' : 'Resend verification email'}
              </Button>
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Two-factor authentication (TOTP)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {totpEnrolled ? (
                    <Chip size="small" color="success" label="Enabled" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Not enabled" variant="outlined" />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                {!totpEnrolled ? (
                  <Button variant="contained" onClick={openMfaEnroll} disabled={!emailVerified}>
                    Enable 2FA
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setUnenrollPassword('');
                      setUnenrollOpen(true);
                    }}
                  >
                    Disable 2FA
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventAvailableIcon color="primary" fontSize="small" />
            <Typography variant="h6">Integrations</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Connect Google Calendar to sync agile tasks as events. Refresh tokens are stored securely
            server-side for background sync.
          </Typography>
          {calendarError && (
            <Alert severity="error" onClose={() => setCalendarError(null)}>
              {calendarError}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Google Calendar
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {calendarConnected ? (
                    <Chip size="small" color="success" label="Connected" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Disconnected" variant="outlined" />
                  )}
                </Stack>
              </Box>
              <Button
                variant={calendarConnected ? 'outlined' : 'contained'}
                startIcon={<GoogleIcon />}
                onClick={handleConnectGoogleCalendar}
                disabled={calendarConnecting || calendarConnected}
              >
                {calendarConnecting
                  ? 'Connecting…'
                  : calendarConnected
                    ? 'Connected'
                    : 'Connect Google Calendar'}
              </Button>
            </Stack>
          </Paper>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button type="button" variant="outlined" onClick={loadProfile} disabled={saving}>
            Reset
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={mfaDialogOpen}
        onClose={() => {
          if (!mfaBusy) closeMfaDialog();
        }}
        disableEscapeKeyDown={mfaBusy}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle>Enable two-factor authentication</DialogTitle>
        <DialogContent dividers>
          {mfaStep === 'password' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Confirm your password to continue. We will then show a QR code to scan with your authenticator app.
              </Typography>
              <TextField
                label="Current password"
                type="password"
                fullWidth
                autoComplete="current-password"
                value={mfaPassword}
                onChange={(e) => setMfaPassword(e.target.value)}
              />
            </Stack>
          )}
          {mfaStep === 'scan' && (
            <Stack spacing={2} sx={{ pt: 1 }} alignItems="center">
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Scan this code with your authenticator app, then enter the 6-digit code to confirm.
              </Typography>
              {mfaQrUri ? (
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
                  <QRCodeSVG value={mfaQrUri} size={200} level="M" marginSize={2} title="TOTP enrollment QR" />
                </Box>
              ) : null}
              <Typography variant="caption" color="text.secondary" alignSelf="stretch">
                If you cannot scan, enter this secret manually (spaces optional):
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={mfaSecretKey}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 14 } }}
                helperText="Type the key above into your app as a time-based account."
              />
              <TextField
                label="6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                inputProps={{ inputMode: 'numeric', maxLength: 6, 'aria-label': 'Authenticator code' }}
                autoComplete="one-time-code"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMfaDialog} disabled={mfaBusy}>
            Cancel
          </Button>
          {mfaStep === 'password' ? (
            <Button variant="contained" onClick={handleMfaPasswordContinue} disabled={mfaBusy}>
              {mfaBusy ? <CircularProgress size={22} /> : 'Continue'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleMfaConfirmEnroll} disabled={mfaBusy}>
              {mfaBusy ? <CircularProgress size={22} /> : 'Verify & enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={unenrollOpen} onClose={() => !mfaBusy && setUnenrollOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Disable two-factor authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your password to remove the authenticator from your account.
          </Typography>
          <TextField
            label="Current password"
            type="password"
            fullWidth
            autoComplete="current-password"
            value={unenrollPassword}
            onChange={(e) => setUnenrollPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnenrollOpen(false)} disabled={mfaBusy}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleUnenrollTotp} disabled={mfaBusy}>
            {mfaBusy ? <CircularProgress size={22} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
